import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Avatar,
  Chip,
  Paper,
  Divider,
  IconButton,
  Alert,
  TextField,
  Stack,
  Tab,
  Tabs,
  Badge,
  alpha,
  List,
  ListItemButton,
  ListItemText
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  SwapHoriz as SwapHorizIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  WorkOutline as WorkOutlineIcon,
  NotificationsActive as NotificationsActiveIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  ChevronRight as ChevronRightIcon,
  EventBusy as EventBusyIcon,
  Person
} from '@mui/icons-material';
import moment from 'moment';
import { Employee, Schedule } from '../../../pages/Dashboard/Dashboard';
import { getShift } from '../../../lib/connector/shiftQueries';

type RequestType = 'SWAP' | 'OFF' | 'JOIN';
type RequestStatus = 'pending' | 'approved' | 'deny' | 'pickedup' | 'expired';

interface Shift {
  scheduleId: string,
  shiftId: string,
  shiftName: string,
  daysOfWeek: string[],
  startTime: string,
  endTime: string,
  position: string,
  staffNeeded: string,
  assignedStaff: string[],
  isRecurring: boolean,
  shiftDate: string,
  notes: string
}

interface Position {
  scheduleId: string,
  position: string,
  positionName: string,
  employees: string[]
}

export interface BaseRequest {
  scheduleId: string;
  requestId: string;
  dateTimeRequested: string;
  requestType: RequestType;
  requesterId: string;
  requesterName: string,
  status: RequestStatus;
  requestDetails: Record<string, any>;
}

interface SwapRequest extends BaseRequest {
  requestType: 'SWAP';
  requestDetails: {
    swapDuration: string;
    swapReason: string;
    swapTarget: string;
    swapDate: string;
    shiftId: string;
  }
}

interface RequestOffRequest extends BaseRequest {
  requestType: 'OFF';
  requestDetails: {
    offReason: string;
    offDate: string;
    shiftId: string;
  }
}

interface EmployeeJoinRequest extends BaseRequest {
  requestType: 'JOIN';
  requestDetails: {
    userId: string;
    joinDate: string;
  }
}

export type Request = SwapRequest | RequestOffRequest | EmployeeJoinRequest;

interface ManagerApprovalModalProps {
  open: boolean;
  onClose: () => void;
  onApprove: (request: BaseRequest, note?: string) => Promise<void>;
  onDeny: (request: BaseRequest, reason: string) => Promise<void>;
  onBulkApprove?: (requestIds: string[], note?: string) => Promise<void>;
  onBulkDeny?: (requestIds: string[], reason: string) => Promise<void>;
  onPickUpShift?: (request: BaseRequest) => Promise<void>;
  schedule: Schedule | null;
  positions: Position[] | null
  employees: Employee[] | null,
  userData: Record<string, any>,
  requests: Request[] | null,
}

const ManagerApprovalModal: React.FC<ManagerApprovalModalProps> = ({
  open,
  onClose,
  onApprove,
  onDeny,
  onBulkApprove,
  onBulkDeny,
  onPickUpShift,
  schedule,
  positions,
  employees,
  userData,
  requests: initialRequests
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [shiftDetails, setShiftDetails] = useState<Shift | null>(null);
  const [shiftPosition, setShiftPosition] = useState<Position | null>(null);
  const [targetStaff, setTargetStaff] = useState<Employee | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDenyForm, setShowDenyForm] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [approvalNote, setApprovalNote] = useState('');
  const [isOpenSwap, setIsOpenSwap] = useState(false);
  const [showPickUpConfirmation, setShowPickUpConfirmation] = useState(false);

  // Categorize requests
  const requests = initialRequests;
  const currentEmployeeData = employees?.filter((e) => e?.userId === userData?.userId)

  const isRequestExpired = (request: Request): boolean => {
    // For pending requests
    if (request.status === 'pending') {
      if (request.requestType === 'SWAP') {

        const swapDate = moment(request.requestDetails.swapDate);
        return swapDate.isBefore(moment(), 'day');
      }
      
      if (request.requestType === 'OFF') {
        const offDate = moment(request.requestDetails.offDate);
        return offDate.isBefore(moment(), 'day');
      }
    }
    
    // For approved swaps that are open for pickup
    if (request.status === 'approved' && request.requestType === 'SWAP') {
      const swapDate = moment(request.requestDetails.swapDate);
      return swapDate.isBefore(moment(), 'day');
    }
    
    return false;
  };

  const pendingRequests = requests?.filter(r => {
    if ((currentEmployeeData?.length ?? 0) > 0 && currentEmployeeData?.[0]?.accessLevel === 'staff') {
      return r.status === 'pending' && r.requesterId === userData.userId;
    }
    return r.status === 'pending';
  }) || [];

  const swapRequests = pendingRequests.filter(r => r.requestType === 'SWAP' && !isRequestExpired(r));

  const allSwapRequests = pendingRequests.filter(r => r.requestType === 'SWAP');

  const timeOffRequests = pendingRequests.filter(r => r.requestType === 'OFF' && !isRequestExpired(r));

  const allTimeOffRequests = pendingRequests.filter(r => r.requestType === 'OFF');

  const joinRequests = pendingRequests.filter(r => r.requestType === 'JOIN');

  const processedRequests = requests?.filter(r => {
    if ((currentEmployeeData?.length ?? 0) > 0 && currentEmployeeData?.[0]?.accessLevel === 'staff') {
      return r.status !== 'pending' && r.requesterId === userData.userId;
    }
    return r.status !== 'pending';
  }) || [];

  const approvedSwapRequestsForMe = requests?.filter(r =>
    r.requestType === 'SWAP' &&
    r.status === 'approved' &&
    r.requesterId !== userData.userId &&
    (r.requestDetails.swapTarget === userData.userId || r.requestDetails.swapTarget === 'any') &&
    !isRequestExpired(r)
  ) || [];

  const allApprovedSwapRequestsForMe = requests?.filter(r =>
    r.requestType === 'SWAP' &&
    r.status === 'approved' &&
    r.requesterId !== userData.userId &&
    (r.requestDetails.swapTarget === userData.userId || r.requestDetails.swapTarget === 'any')
  ) || [];

  React.useEffect(() => {
    if (schedule?.scheduleId && open && selectedRequest && selectedRequest.requestType === "SWAP") {
      getShift(schedule.scheduleId, selectedRequest.requestDetails.shiftId).then((response) => {
        setShiftDetails(response[0])
        setShiftPosition(
          positions
            ? positions.find((p) => p.position === response[0].position) || null
            : null
        )
        setTargetStaff(
          employees
            ? employees.find((e) => e.userId === selectedRequest.requestDetails.swapTarget) || null
            : null
        )
      })
    } else if (schedule?.scheduleId && open && selectedRequest && selectedRequest.requestType === "OFF") {
      getShift(schedule.scheduleId, selectedRequest.requestDetails.shiftId).then((response) => {
        setShiftDetails(response[0])
        setShiftPosition(
          positions
            ? positions.find((p) => p.position === response[0].position) || null
            : null
        )
      })
    }
  }, [selectedRequest])

  const getRequestTypeInfo = (type: RequestType) => {
    switch (type) {
      case 'SWAP':
        return {
          title: 'Shift Swap',
          icon: <SwapHorizIcon />,
          color: 'primary' as const,
          bgColor: 'primary.light'
        };
      case 'OFF':
        return {
          title: 'Request Off',
          icon: <SwapHorizIcon />,
          color: 'warning' as const,
          bgColor: 'warning.light'
        };
      case 'JOIN':
        return {
          title: 'Join Request',
          icon: <PersonIcon />,
          color: 'success' as const,
          bgColor: 'success.light'
        };
    }
  };

  const formatTime = (time: string) => {
    const [hour, min] = time.split(':').map(Number);
    return moment().hours(hour).minutes(min).format('h:mm A');
  };

  const handleApproveRequest = async () => {
    if (!selectedRequest) return;

    setIsProcessing(true);
    try {
      await onApprove(selectedRequest, approvalNote.trim() || undefined);
      setApprovalNote('');
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error approving request:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDenyRequest = async () => {
    if (!selectedRequest) return;

    setIsProcessing(true);
    try {
      await onDeny(selectedRequest, denyReason.trim());
      setDenyReason('');
      setShowDenyForm(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error denying request:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setSelectedRequests([]);
    setSelectedRequest(null);
    setShowDenyForm(false);
    setDenyReason('');
    setApprovalNote('');
    onClose();
  };

  const handleCloseDetailsModal = () => {
    setSelectedRequest(null);
    setShowDenyForm(false);
    setDenyReason('');
    setApprovalNote('');
    setIsOpenSwap(false);
    setShowPickUpConfirmation(false);
  };

  const handlePickUpShift = async () => {
    if (!selectedRequest || !onPickUpShift) return;

    setIsProcessing(true);
    try {
      await onPickUpShift(selectedRequest);
      setShowPickUpConfirmation(false);
      handleCloseDetailsModal();
    } catch (error) {
      console.error('Error picking up shift:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const RequestListItem = ({ request, isOpen = false }: { request: Request; isOpen?: boolean }) => {
    const typeInfo = getRequestTypeInfo(request.requestType);
    const isSelected = selectedRequests.includes(request.requestId);

    return (
      <Paper
        variant="outlined"
        sx={{
          mb: 1.5,
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'divider',
          bgcolor: isSelected ? alpha('#1976d2', 0.05) : 'background.paper',
        }}
      >
        <ListItemButton
          onClick={() => {
            setSelectedRequest(request);
            setIsOpenSwap(isOpen);
          }}
          sx={{ py: 1.5, px: 2 }}
        >

          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {request.requesterName}
                </Typography>
                <Chip
                  label={typeInfo.title}
                  size="small"
                  color={typeInfo.color}
                />
                {request.status === 'pending' && (
                  <>
                    {isRequestExpired(request) ? (
                      <Chip
                        label="Expired"
                        size="small"
                        variant="outlined"
                        color="error"
                      />
                    ) : (
                      <Chip
                        label="Pending"
                        size="small"
                        color="error"
                      />
                    )}
                  </>
                )}
                {request.status === 'approved' && request.requestType === "SWAP" && 
                request.requestDetails.swapTarget === "any" &&(
                  <>
                    {isRequestExpired(request) ? (
                      <Chip
                        label="Expired"
                        size="small"
                        variant="outlined"
                        color="error"
                      />
                    ) : (
                      <></>
                    )}
                  </>
                )}
                {request.status === 'expired' && (
                  <Chip
                    label="Expired"
                    size="small"
                    variant="outlined"
                    color="error"
                  />
                )}
              </Box>
            }
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: '60px', textAlign: 'right' }}>
              {moment(request.dateTimeRequested).fromNow()}
            </Typography>
            <ChevronRightIcon color="action" />
          </Box>
        </ListItemButton>
      </Paper>
    );
  };

  const TabContent = ({ requests, type }: { requests: Request[]; type: string }) => {
    const currentTabRequests = type === 'all' ? pendingRequests : requests;
    const hasRequests = currentTabRequests.length > 0;
    const isOpenSwapTab = type === 'SWAP' && requests === approvedSwapRequestsForMe;

    return (
      <Box>
        {!hasRequests ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Avatar sx={{ width: 80, height: 80, bgcolor: 'grey.100', mx: 'auto', mb: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 40, color: 'grey.400' }} />
            </Avatar>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {type === 'processed' ? 'No Processed Requests' : 'No Pending Requests'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {type === 'processed'
                ? 'Approved or denied requests will appear here'
                : 'All caught up! No requests need your attention.'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {currentTabRequests.map(request => (
              <RequestListItem key={request.requestId} request={request} isOpen={isOpenSwapTab} />
            ))}
          </List>
        )}
      </Box>
    );
  };

  const RequestDetailsModal = () => {
    if (!selectedRequest) return null;

    const typeInfo = getRequestTypeInfo(selectedRequest.requestType);

    return (
      <Dialog
        open={Boolean(selectedRequest)}
        onClose={handleCloseDetailsModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                {typeInfo.icon}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Request Details
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Submitted {moment(selectedRequest.dateTimeRequested).fromNow()} - {moment(selectedRequest.dateTimeRequested).format("(YYYY-MM-DD, h:mm)")}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseDetailsModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={3}>
            {/* Requester Info */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} gutterBottom display="block">
                REQUESTED BY
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {selectedRequest.requesterName || 'Employee Name'}
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Chip label={typeInfo.title} color={typeInfo.color} />
                </Box>
              </Box>
            </Box>

            {selectedRequest.requestType !== 'JOIN' && (<Divider />)}

            {/* Shift Details */}
            {selectedRequest.requestType !== 'JOIN' && (<Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} gutterBottom display="block">
                SHIFT DETAILS
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    <WorkOutlineIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {shiftDetails?.shiftName || 'Shift Name'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {shiftPosition?.positionName || 'Position'}
                    </Typography>
                  </Box>
                </Box>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {selectedRequest.requestType === 'SWAP'
                        ? moment(selectedRequest.requestDetails.swapDate).format('dddd, MMMM D, YYYY') :
                        selectedRequest.requestType === 'OFF' ?
                          moment(selectedRequest.requestDetails.offDate).format('dddd, MMMM D, YYYY') :
                          ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {shiftDetails?.startTime && shiftDetails.endTime &&
                        `${formatTime(shiftDetails.startTime)} - ${formatTime(shiftDetails.endTime)}`}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Box>)}

            {/* Swap Target Info */}
            {selectedRequest.requestType === 'SWAP' && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} gutterBottom display="block">
                  SWAP WITH
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {targetStaff?.name ? targetStaff?.name : "Any available staff can pick up"}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
                {targetStaff?.name ? <></> :
                  <Typography variant="caption">
                    **If another staff member does not pick up this shift, then the original staff member is expected to come in
                  </Typography>}
              </Box>
            )}

            {/* Status Info */}
            {selectedRequest.status !== 'pending' && (
              <Alert
                severity={selectedRequest.status === 'approved' ? 'success' : 'error'}
                icon={selectedRequest.status === 'approved' ? <CheckCircleIcon /> : <CancelIcon />}
              >
                This request has been {selectedRequest.status}
              </Alert>
            )}

            {/* Expired Request Alert */}
            {selectedRequest.status === 'pending' && isRequestExpired(selectedRequest) && (
              <Alert
                severity="error"
                icon={<EventBusyIcon />}
              >
                This request has expired. The requested date has already passed.
              </Alert>
            )}

            {/* Actions for Pending Requests */}
            {currentEmployeeData !== undefined && currentEmployeeData[0].accessLevel !== "staff" && selectedRequest.status === 'pending' && (
              <Box>
                <Divider sx={{ mb: 2 }} />
                {!showDenyForm ? (
                  <>
                    {selectedRequest.requestType !== 'JOIN' && <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={3}
                      label="Approval Note (Optional)"
                      placeholder="Add a note for the employee..."
                      value={approvalNote}
                      onChange={(e) => setApprovalNote(e.target.value)}
                      sx={{ mb: 2 }}
                    />}
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<ThumbDownIcon />}
                        onClick={handleDenyRequest}
                        disabled={isProcessing || isRequestExpired(selectedRequest)}
                        size="large"
                      >
                        Deny Request
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<ThumbUpIcon sx={{ color: 'background.paper' }} />}
                        onClick={handleApproveRequest}
                        disabled={isProcessing || isRequestExpired(selectedRequest)}
                        size="large"
                        sx={{ color: 'background.paper' }}
                      >
                        Approve Request
                      </Button>
                    </Stack>
                  </>
                ) : (
                  <></>
                )}
              </Box>
            )}

            {/* Actions for Open Shift Swaps */}
            {isOpenSwap && selectedRequest.status === 'approved' && (
              <Box>
                <Divider sx={{ mb: 2 }} />
                {isRequestExpired(selectedRequest) && (
                  <Alert
                    severity="error"
                    icon={<EventBusyIcon />}
                    sx={{ mb: 2 }}
                  >
                    This swap has expired. The swap date has already passed.
                  </Alert>
                )}
                {!showPickUpConfirmation ? (
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    startIcon={<ThumbUpIcon sx={{ color: 'background.paper' }} />}
                    onClick={() => setShowPickUpConfirmation(true)}
                    disabled={isProcessing || isRequestExpired(selectedRequest)}
                    size="large"
                    sx={{ color: 'background.paper' }}
                  >
                    Pick Up Shift
                  </Button>
                ) : (
                  <>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Are you sure you want to pick up this shift?
                    </Alert>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        onClick={() => setShowPickUpConfirmation(false)}
                        disabled={isProcessing}
                        size="large"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handlePickUpShift}
                        disabled={isProcessing}
                        size="large"
                        sx={{ color: 'background.paper' }}
                      >
                        {isProcessing ? 'Picking Up...' : 'Confirm'}
                      </Button>
                    </Stack>
                  </>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    )
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                <NotificationsActiveIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Approvals
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {pendingRequests.length} pending • {processedRequests.length} processed
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {currentEmployeeData !== undefined && currentEmployeeData[0]?.accessLevel !== "staff" ? <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            sx={{ px: 3, pt: 2, borderBottom: 1, borderColor: 'divider' }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              label={
                <Badge badgeContent={swapRequests.length} color="primary">
                  <Box sx={{ pr: swapRequests.length > 0 ? 2 : 0 }}>Swaps</Box>
                </Badge>
              }
              icon={<SwapHorizIcon />}
              iconPosition="start"
            />
            <Tab
              label={
                <Badge badgeContent={timeOffRequests.length} color="primary">
                  <Box sx={{ pr: timeOffRequests.length > 0 ? 2 : 0 }}>Time Off</Box>
                </Badge>
              }
              icon={<EventBusyIcon />}
              iconPosition="start"
            />
            <Tab
              label={
                <Badge badgeContent={joinRequests.length} color="primary">
                  <Box sx={{ pr: joinRequests.length > 0 ? 2 : 0 }}>Join Requests</Box>
                </Badge>
              }
              icon={<PersonIcon />}
              iconPosition="start"
            />
            <Tab
              label={
                <Badge badgeContent={approvedSwapRequestsForMe.length} color="primary">
                  <Box sx={{ pr: approvedSwapRequestsForMe.length > 0 ? 2 : 0 }}>Open Shift Swaps</Box>
                </Badge>
              }
              icon={<SwapHorizIcon />}
              iconPosition="start"
            />
            <Tab
              label={
                <Badge badgeContent={processedRequests.length} color="default">
                  <Box sx={{ pr: processedRequests.length > 0 ? 2 : 0 }}>Processed</Box>
                </Badge>
              }
              icon={<CheckCircleIcon />}
              iconPosition="start"
            />
          </Tabs> : <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            sx={{ px: 3, pt: 2, borderBottom: 1, borderColor: 'divider' }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              label={
                <Badge badgeContent={pendingRequests.length} color="primary">
                  <Box sx={{ pr: pendingRequests.length > 0 ? 2 : 0 }}>My Requests</Box>
                </Badge>
              }
              icon={<Person />}
              iconPosition="start"
            />
            <Tab
              label={
                <Badge badgeContent={approvedSwapRequestsForMe.length} color="primary">
                  <Box sx={{ pr: approvedSwapRequestsForMe.length > 0 ? 2 : 0 }}>Open Shift Swaps</Box>
                </Badge>
              }
              icon={<SwapHorizIcon />}
              iconPosition="start"
            />
            <Tab
              label={
                <Badge badgeContent={processedRequests.length} color="default">
                  <Box sx={{ pr: processedRequests.length > 0 ? 2 : 0 }}>Processed</Box>
                </Badge>
              }
              icon={<CheckCircleIcon />}
              iconPosition="start"
            />
          </Tabs>}

          {currentEmployeeData !== undefined && currentEmployeeData[0]?.accessLevel !== "staff" ? <Box sx={{ p: 3 }}>
            {activeTab === 0 && <TabContent requests={allSwapRequests} type="SWAP" />}
            {activeTab === 1 && <TabContent requests={allTimeOffRequests} type="OFF" />}
            {activeTab === 2 && <TabContent requests={joinRequests} type="JOIN" />}
            {activeTab === 3 && <TabContent requests={allApprovedSwapRequestsForMe} type="SWAP" />}
            {activeTab === 4 && <TabContent requests={processedRequests} type="processed" />}
          </Box> : <Box sx={{ p: 3 }}>
            {activeTab === 0 && <TabContent requests={pendingRequests} type="pending" />}
            {activeTab === 1 && <TabContent requests={allApprovedSwapRequestsForMe} type="SWAP" />}
            {activeTab === 2 && <TabContent requests={processedRequests} type="processed" />}
          </Box>}
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Button onClick={handleClose} variant="outlined" size="large">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <RequestDetailsModal />
    </>
  );
};

export default ManagerApprovalModal;